import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import axios from '../config/api';
import { readCurrentCoordinates } from '../utils/deviceLocation';
import AppTileLayer, { mapMotionOptions } from './AppTileLayer';
import type { DeliveryDropoff } from '../utils/deliveryDropoff';
import { formatDrivingKm } from '../utils/deliveryDropoff';
import { deliveryFeeJod } from '../utils/deliveryFee';
import { formatCurrency } from '../utils/format';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const AMMAN: [number, number] = [31.9539, 35.9106];

export type CafeOrigin = {
  lat: number;
  lng: number;
  name?: string;
};

type PlaceSuggestion = {
  place_id: string;
  description: string;
  lat?: number;
  lng?: number;
};

function Recenter({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, Math.max(map.getZoom(), 15));
    const timer = window.setTimeout(() => map.invalidateSize(), 80);
    return () => window.clearTimeout(timer);
  }, [center, map]);
  return null;
}

function MapPickers({
  onPick,
}: {
  onPick: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(event) {
      onPick(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

export default function DeliveryLocationPicker({
  cafeId,
  cafeOrigin,
  value,
  onChange,
}: {
  cafeId: string;
  cafeOrigin: CafeOrigin | null;
  value: DeliveryDropoff | null;
  onChange: (next: DeliveryDropoff | null) => void;
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [error, setError] = useState('');
  const quoteSeq = useRef(0);
  const searchTimer = useRef<number | null>(null);

  const center = useMemo<[number, number]>(() => {
    if (value) return [value.lat, value.lng];
    if (cafeOrigin) return [cafeOrigin.lat, cafeOrigin.lng];
    return AMMAN;
  }, [value, cafeOrigin]);

  useEffect(() => {
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
    };
  }, []);

  const applyQuote = async (dropoff: DeliveryDropoff) => {
    const seq = ++quoteSeq.current;
    setQuoting(true);
    setError('');
    try {
      const res = await axios.post('/api/delivery/quote', {
        cafe_id: cafeId,
        lat: dropoff.lat,
        lng: dropoff.lng,
        address: dropoff.address,
        place_id: dropoff.placeId,
      });
      if (seq !== quoteSeq.current) return;
      onChange({
        ...dropoff,
        distanceKm: Number(res.data.distance_km),
        durationMin: Number(res.data.duration_min),
      });
    } catch (err: any) {
      if (seq !== quoteSeq.current) return;
      onChange({ ...dropoff, distanceKm: null, durationMin: null });
      setError(
        err?.response?.data?.error ||
          'Google Maps could not measure this delivery distance yet.'
      );
    } finally {
      if (seq === quoteSeq.current) setQuoting(false);
    }
  };

  const setPin = async (lat: number, lng: number, preset?: Partial<DeliveryDropoff>) => {
    let address = preset?.address || '';
    let placeId = preset?.placeId || null;
    if (!address) {
      try {
        const geo = await axios.get('/api/delivery/geocode', { params: { lat, lng } });
        address = String(geo.data.address || '');
        placeId = geo.data.place_id || placeId;
      } catch {
        address = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      }
    }
    const dropoff: DeliveryDropoff = {
      lat,
      lng,
      address,
      placeId,
      distanceKm: null,
      durationMin: null,
    };
    onChange(dropoff);
    await applyQuote(dropoff);
  };

  const searchPlaces = (raw: string) => {
    setQuery(raw);
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    if (raw.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    searchTimer.current = window.setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get('/api/delivery/places', { params: { q: raw.trim() } });
        setSuggestions(res.data.predictions || []);
      } catch (err: any) {
        setSuggestions([]);
        setError(err?.response?.data?.error || 'Google Maps search failed.');
      } finally {
        setSearching(false);
      }
    }, 280);
  };

  const pickSuggestion = async (suggestion: PlaceSuggestion) => {
    setSuggestions([]);
    setQuery(suggestion.description);
    if (Number.isFinite(Number(suggestion.lat)) && Number.isFinite(Number(suggestion.lng))) {
      await setPin(Number(suggestion.lat), Number(suggestion.lng), {
        address: suggestion.description,
        placeId: suggestion.place_id,
      });
      return;
    }
    try {
      const details = await axios.get('/api/delivery/place', {
        params: { place_id: suggestion.place_id },
      });
      await setPin(Number(details.data.lat), Number(details.data.lng), {
        address: details.data.address || suggestion.description,
        placeId: details.data.place_id || suggestion.place_id,
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not open that Google Maps place.');
    }
  };

  const useMyLocation = () => {
    setError('');
    readCurrentCoordinates()
      .then(({ lat, lng }) => {
        void setPin(lat, lng);
      })
      .catch((err: Error) => {
        setError(err.message || 'Could not read your current location. Drop a pin on the map instead.');
      });
  };

  const kmLabel = formatDrivingKm(value?.distanceKm);
  const cafeReady = Boolean(cafeOrigin);

  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2">
      <p className="text-xs text-amber-900">
        Offerat uses Google Maps driving distance from the café to your pin.
      </p>
      {!cafeReady && (
        <p className="text-xs text-red-700">
          This café has no map location yet, so delivery distance cannot be calculated.
        </p>
      )}
      <div className="relative">
        <input
          type="search"
          value={query}
          onChange={(e) => searchPlaces(e.target.value)}
          placeholder="Search building, street, or area in Jordan"
          className="w-full px-3 py-2 border rounded bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          disabled={!cafeReady}
        />
        {searching && (
          <p className="absolute right-2 top-2 text-[11px] text-gray-500">Searching…</p>
        )}
        {suggestions.length > 0 && (
          <ul className="absolute z-20 left-0 right-0 mt-1 max-h-40 overflow-auto rounded border bg-white shadow">
            {suggestions.map((item) => (
              <li key={item.place_id}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50"
                  onClick={() => void pickSuggestion(item)}
                >
                  {item.description}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="relative z-0 h-52 border border-amber-200">
        <MapContainer
          center={center}
          zoom={14}
          scrollWheelZoom
          {...mapMotionOptions}
          className="z-0"
          style={{ height: '100%', width: '100%', zIndex: 0 }}
        >
          <AppTileLayer />
          <Recenter center={center} />
          {cafeReady && <MapPickers onPick={(lat, lng) => void setPin(lat, lng)} />}
          {value && (
            <Marker
              position={[value.lat, value.lng]}
              draggable
              eventHandlers={{
                dragend: (event) => {
                  const next = event.target.getLatLng();
                  void setPin(next.lat, next.lng);
                },
              }}
            />
          )}
        </MapContainer>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={useMyLocation}
          disabled={!cafeReady}
          className="text-xs font-semibold text-amber-800 underline disabled:text-gray-400"
        >
          Use my location
        </button>
        {quoting && <span className="text-xs text-gray-600">Measuring Google driving km…</span>}
        {!quoting && kmLabel && value?.durationMin != null && (
          <span className="text-xs font-semibold text-amber-900">
            {kmLabel} · about {value.durationMin} min · {formatCurrency(deliveryFeeJod(value.distanceKm))}
          </span>
        )}
      </div>
      {value?.address && (
        <p className="text-xs text-gray-700">Drop-off: {value.address}</p>
      )}
      {error && <p className="text-xs text-red-700">{error}</p>}
    </div>
  );
}
